/**
 * PureOne Advanced Interactions
 * Implements interactive elements like before/after slider, video controls, tabs, etc.
 */

class PureOneInteractions {
  constructor() {
    this.init();
  }

  init() {
    // Initialize all interactive components
    this.setupBeforeAfterSlider();
    this.setupVideoPlayers();
    this.setupTabSwitching();
    this.setupAccordions();
    this.setupCarousels();
    this.setupFormValidations();
    this.setupShareFunctionality();
    this.setupTooltips();
    this.setupLoadingStates();
  }

  setupBeforeAfterSlider() {
    const sliders = document.querySelectorAll('[data-before-after]');

    sliders.forEach(slider => {
      const before = slider.querySelector('.before-image');
      const after = slider.querySelector('.after-image');
      const handle = slider.querySelector('.slider-handle') || this.createSliderHandle(slider);

      if (!before || !after) return;

      let isDragging = false;

      const updateSlider = (x) => {
        const rect = slider.getBoundingClientRect();
        const percent = Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100));

        after.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
        handle.style.left = `${percent}%`;
      };

      // Mouse events
      handle.addEventListener('mousedown', () => isDragging = true);
      document.addEventListener('mouseup', () => isDragging = false);
      document.addEventListener('mousemove', (e) => {
        if (isDragging) updateSlider(e.clientX);
      });

      // Touch events
      handle.addEventListener('touchstart', () => isDragging = true);
      document.addEventListener('touchend', () => isDragging = false);
      document.addEventListener('touchmove', (e) => {
        if (isDragging && e.touches[0]) {
          updateSlider(e.touches[0].clientX);
        }
      });

      // Click to position
      slider.addEventListener('click', (e) => {
        updateSlider(e.clientX);
      });
    });
  }

  createSliderHandle(slider) {
    const handle = document.createElement('div');
    handle.className = 'slider-handle';
    handle.innerHTML = '<span>&larr;</span><span>&rarr;</span>';
    handle.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
      height: 40px;
      background: white;
      border-radius: 50%;
      cursor: grab;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
      transition: transform 0.2s ease;
    `;

    handle.addEventListener('mouseenter', () => {
      handle.style.transform = 'translate(-50%, -50%) scale(1.1)';
    });

    handle.addEventListener('mouseleave', () => {
      handle.style.transform = 'translate(-50%, -50%) scale(1)';
    });

    slider.appendChild(handle);
    return handle;
  }

  setupVideoPlayers() {
    const videos = document.querySelectorAll('[data-video]');

    videos.forEach(container => {
      const video = container.querySelector('video');
      const playBtn = container.querySelector('.video-play-btn') || this.createPlayButton(container);

      if (!video) return;

      // Create custom controls
      const controls = this.createVideoControls(container, video);

      playBtn.addEventListener('click', () => {
        if (video.paused) {
          video.play();
          playBtn.style.opacity = '0';
          controls.classList.add('playing');
        } else {
          video.pause();
          playBtn.style.opacity = '1';
          controls.classList.remove('playing');
        }
      });

      video.addEventListener('ended', () => {
        playBtn.style.opacity = '1';
        controls.classList.remove('playing');
      });

      // Show/hide controls on hover
      container.addEventListener('mouseenter', () => {
        if (!video.paused) controls.style.opacity = '1';
      });

      container.addEventListener('mouseleave', () => {
        if (!video.paused) controls.style.opacity = '0';
      });
    });
  }

  createPlayButton(container) {
    const button = document.createElement('button');
    button.className = 'video-play-btn';
    button.innerHTML = `
      <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
        <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.7)"/>
        <path d="M24 20L40 30L24 40V20Z" fill="white"/>
      </svg>
    `;
    button.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: none;
      border: none;
      cursor: pointer;
      transition: opacity 0.3s ease, transform 0.2s ease;
      z-index: 5;
    `;

    button.addEventListener('mouseenter', () => {
      button.style.transform = 'translate(-50%, -50%) scale(1.1)';
    });

    button.addEventListener('mouseleave', () => {
      button.style.transform = 'translate(-50%, -50%) scale(1)';
    });

    container.appendChild(button);
    return button;
  }

  createVideoControls(container, video) {
    const controls = document.createElement('div');
    controls.className = 'video-controls';
    controls.style.cssText = `
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 10px;
      background: linear-gradient(transparent, rgba(0,0,0,0.7));
      opacity: 0;
      transition: opacity 0.3s ease;
      display: flex;
      align-items: center;
      gap: 10px;
    `;

    // Progress bar
    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
      flex: 1;
      height: 4px;
      background: rgba(255,255,255,0.3);
      border-radius: 2px;
      cursor: pointer;
      position: relative;
    `;

    const progress = document.createElement('div');
    progress.style.cssText = `
      height: 100%;
      background: var(--brand-primary);
      border-radius: 2px;
      width: 0;
      transition: width 0.1s linear;
    `;

    progressBar.appendChild(progress);
    controls.appendChild(progressBar);

    // Update progress
    video.addEventListener('timeupdate', () => {
      const percent = (video.currentTime / video.duration) * 100;
      progress.style.width = `${percent}%`;
    });

    // Seek on click
    progressBar.addEventListener('click', (e) => {
      const rect = progressBar.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      video.currentTime = percent * video.duration;
    });

    container.appendChild(controls);
    return controls;
  }

  setupTabSwitching() {
    const tabContainers = document.querySelectorAll('[data-tabs]');

    tabContainers.forEach(container => {
      const tabs = container.querySelectorAll('[data-tab]');
      const panels = container.querySelectorAll('[data-panel]');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const targetPanel = tab.dataset.tab;

          // Update tabs
          tabs.forEach(t => {
            t.classList.remove('active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('active');
          tab.setAttribute('aria-selected', 'true');

          // Update panels
          panels.forEach(panel => {
            if (panel.dataset.panel === targetPanel) {
              panel.classList.add('active');
              panel.style.display = 'block';

              // Animate in
              requestAnimationFrame(() => {
                panel.style.opacity = '0';
                panel.style.transform = 'translateY(10px)';

                requestAnimationFrame(() => {
                  panel.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
                  panel.style.opacity = '1';
                  panel.style.transform = 'translateY(0)';
                });
              });
            } else {
              panel.classList.remove('active');
              panel.style.display = 'none';
            }
          });
        });
      });

      // Activate first tab by default
      if (tabs.length > 0) tabs[0].click();
    });
  }

  setupAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const items = accordion.querySelectorAll('[data-accordion-item]');

      items.forEach(item => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const content = item.querySelector('[data-accordion-content]');

        if (!trigger || !content) return;

        // Set initial state
        content.style.maxHeight = '0';
        content.style.overflow = 'hidden';
        content.style.transition = 'max-height 0.3s ease, padding 0.3s ease';

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('open');

          // Close all other items if exclusive
          if (accordion.dataset.accordion === 'exclusive') {
            items.forEach(i => {
              if (i !== item) {
                i.classList.remove('open');
                const c = i.querySelector('[data-accordion-content]');
                if (c) c.style.maxHeight = '0';
              }
            });
          }

          // Toggle current item
          if (isOpen) {
            item.classList.remove('open');
            content.style.maxHeight = '0';
          } else {
            item.classList.add('open');
            content.style.maxHeight = content.scrollHeight + 'px';
          }

          // Rotate icon if exists
          const icon = trigger.querySelector('.accordion-icon');
          if (icon) {
            icon.style.transform = isOpen ? 'rotate(0)' : 'rotate(180deg)';
          }
        });
      });
    });
  }

  setupCarousels() {
    // Enhanced carousel functionality
    const carousels = document.querySelectorAll('[data-carousel]');

    carousels.forEach(carousel => {
      const track = carousel.querySelector('.carousel-track');
      const slides = carousel.querySelectorAll('.carousel-slide');
      const prevBtn = carousel.querySelector('.carousel-prev');
      const nextBtn = carousel.querySelector('.carousel-next');
      const indicators = carousel.querySelector('.carousel-indicators');

      if (!track || slides.length === 0) return;

      let currentIndex = 0;
      const autoplay = carousel.dataset.carouselAutoplay === 'true';
      const duration = parseInt(carousel.dataset.carouselDuration) || 5000;
      let autoplayTimer;

      const goToSlide = (index) => {
        currentIndex = index;
        const offset = -index * 100;
        track.style.transform = `translateX(${offset}%)`;

        // Update indicators
        if (indicators) {
          indicators.querySelectorAll('.indicator').forEach((dot, i) => {
            dot.classList.toggle('active', i === index);
          });
        }

        // Update button states
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === slides.length - 1;
      };

      // Create indicators
      if (indicators && slides.length > 1) {
        slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.className = 'indicator';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => {
            goToSlide(i);
            resetAutoplay();
          });
          indicators.appendChild(dot);
        });
      }

      // Navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          goToSlide(Math.max(0, currentIndex - 1));
          resetAutoplay();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          goToSlide(Math.min(slides.length - 1, currentIndex + 1));
          resetAutoplay();
        });
      }

      // Touch/swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      });

      carousel.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      });

      const handleSwipe = () => {
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            // Swipe left - next slide
            goToSlide(Math.min(slides.length - 1, currentIndex + 1));
          } else {
            // Swipe right - previous slide
            goToSlide(Math.max(0, currentIndex - 1));
          }
          resetAutoplay();
        }
      };

      // Autoplay
      const startAutoplay = () => {
        if (autoplay && slides.length > 1) {
          autoplayTimer = setInterval(() => {
            goToSlide((currentIndex + 1) % slides.length);
          }, duration);
        }
      };

      const resetAutoplay = () => {
        clearInterval(autoplayTimer);
        startAutoplay();
      };

      // Pause on hover
      if (autoplay) {
        carousel.addEventListener('mouseenter', () => clearInterval(autoplayTimer));
        carousel.addEventListener('mouseleave', startAutoplay);
      }

      // Initialize
      goToSlide(0);
      startAutoplay();
    });
  }

  setupFormValidations() {
    const forms = document.querySelectorAll('[data-validate]');

    forms.forEach(form => {
      const inputs = form.querySelectorAll('input, textarea, select');

      inputs.forEach(input => {
        // Real-time validation
        input.addEventListener('blur', () => this.validateField(input));
        input.addEventListener('input', () => {
          if (input.classList.contains('error')) {
            this.validateField(input);
          }
        });
      });

      // Form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        let isValid = true;
        inputs.forEach(input => {
          if (!this.validateField(input)) {
            isValid = false;
          }
        });

        if (isValid) {
          this.showSuccessAnimation(form);
        }
      });
    });
  }

  validateField(input) {
    const value = input.value.trim();
    const type = input.type;
    const required = input.hasAttribute('required');
    let isValid = true;
    let message = '';

    // Remove previous error
    this.clearError(input);

    // Required validation
    if (required && !value) {
      message = 'This field is required';
      isValid = false;
    }

    // Email validation
    else if (type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        message = 'Please enter a valid email address';
        isValid = false;
      }
    }

    // Phone validation
    else if (type === 'tel' && value) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(value)) {
        message = 'Please enter a valid phone number';
        isValid = false;
      }
    }

    // Min length validation
    else if (input.minLength > 0 && value.length < input.minLength) {
      message = `Minimum ${input.minLength} characters required`;
      isValid = false;
    }

    if (!isValid) {
      this.showError(input, message);
    }

    return isValid;
  }

  showError(input, message) {
    input.classList.add('error');

    const errorEl = document.createElement('div');
    errorEl.className = 'error-message';
    errorEl.textContent = message;
    errorEl.style.cssText = `
      color: var(--color-danger, #dc3545);
      font-size: 12px;
      margin-top: 4px;
      animation: slideDown 0.2s ease;
    `;

    input.parentNode.appendChild(errorEl);
  }

  clearError(input) {
    input.classList.remove('error');
    const errorEl = input.parentNode.querySelector('.error-message');
    if (errorEl) errorEl.remove();
  }

  setupShareFunctionality() {
    const shareButtons = document.querySelectorAll('[data-share]');

    shareButtons.forEach(button => {
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl || window.location.href;
        const title = button.dataset.shareTitle || document.title;
        const text = button.dataset.shareText || '';

        // Use native share API if available
        if (navigator.share) {
          try {
            await navigator.share({ title, text, url });
          } catch (err) {
            // User cancelled or error
          }
        } else {
          // Fallback to copying URL
          this.copyToClipboard(url);
          this.showTooltip(button, 'Link copied!');
        }
      });
    });
  }

  copyToClipboard(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  setupTooltips() {
    const tooltipElements = document.querySelectorAll('[data-tooltip]');

    tooltipElements.forEach(element => {
      let tooltip;

      element.addEventListener('mouseenter', () => {
        const text = element.dataset.tooltip;
        const position = element.dataset.tooltipPosition || 'top';

        tooltip = this.createTooltip(text, position);
        document.body.appendChild(tooltip);

        const rect = element.getBoundingClientRect();
        this.positionTooltip(tooltip, rect, position);

        requestAnimationFrame(() => {
          tooltip.style.opacity = '1';
          tooltip.style.transform = 'translate(-50%, 0) scale(1)';
        });
      });

      element.addEventListener('mouseleave', () => {
        if (tooltip) {
          tooltip.style.opacity = '0';
          setTimeout(() => tooltip.remove(), 200);
        }
      });
    });
  }

  createTooltip(text, position) {
    const tooltip = document.createElement('div');
    tooltip.className = 'tooltip';
    tooltip.textContent = text;
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 13px;
      white-space: nowrap;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
      transform: translate(-50%, 0) scale(0.9);
      transition: opacity 0.2s ease, transform 0.2s ease;
    `;
    return tooltip;
  }

  positionTooltip(tooltip, rect, position) {
    const offset = 10;

    switch (position) {
      case 'top':
        tooltip.style.left = `${rect.left + rect.width / 2}px`;
        tooltip.style.top = `${rect.top - offset}px`;
        tooltip.style.transform = 'translate(-50%, -100%) scale(0.9)';
        break;
      case 'bottom':
        tooltip.style.left = `${rect.left + rect.width / 2}px`;
        tooltip.style.top = `${rect.bottom + offset}px`;
        break;
      case 'left':
        tooltip.style.left = `${rect.left - offset}px`;
        tooltip.style.top = `${rect.top + rect.height / 2}px`;
        tooltip.style.transform = 'translate(-100%, -50%) scale(0.9)';
        break;
      case 'right':
        tooltip.style.left = `${rect.right + offset}px`;
        tooltip.style.top = `${rect.top + rect.height / 2}px`;
        tooltip.style.transform = 'translate(0, -50%) scale(0.9)';
        break;
    }
  }

  showTooltip(element, text) {
    const tooltip = this.createTooltip(text, 'top');
    document.body.appendChild(tooltip);

    const rect = element.getBoundingClientRect();
    this.positionTooltip(tooltip, rect, 'top');

    setTimeout(() => {
      tooltip.style.opacity = '1';
      tooltip.style.transform = 'translate(-50%, -100%) scale(1)';
    }, 10);

    setTimeout(() => {
      tooltip.style.opacity = '0';
      setTimeout(() => tooltip.remove(), 200);
    }, 2000);
  }

  setupLoadingStates() {
    const loadingButtons = document.querySelectorAll('[data-loading]');

    loadingButtons.forEach(button => {
      button.addEventListener('click', () => {
        const originalText = button.textContent;
        button.disabled = true;
        button.innerHTML = `
          <span class="loading-spinner"></span>
          ${button.dataset.loadingText || 'Loading...'}
        `;

        // Add spinner styles if not exists
        if (!document.querySelector('#loading-spinner-styles')) {
          const style = document.createElement('style');
          style.id = 'loading-spinner-styles';
          style.textContent = `
            .loading-spinner {
              display: inline-block;
              width: 14px;
              height: 14px;
              border: 2px solid currentColor;
              border-radius: 50%;
              border-top-color: transparent;
              animation: spin 0.6s linear infinite;
              margin-right: 8px;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `;
          document.head.appendChild(style);
        }

        // Simulate loading (replace with actual async operation)
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
          this.showSuccessAnimation(button);
        }, 2000);
      });
    });
  }

  showSuccessAnimation(element) {
    const success = document.createElement('div');
    success.innerHTML = '✓';
    success.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0);
      width: 60px;
      height: 60px;
      background: var(--color-success, #28a745);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 30px;
      font-weight: bold;
      z-index: 9999;
      animation: successPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    `;

    // Add animation styles
    if (!document.querySelector('#success-animation-styles')) {
      const style = document.createElement('style');
      style.id = 'success-animation-styles';
      style.textContent = `
        @keyframes successPop {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(success);

    setTimeout(() => {
      success.style.opacity = '0';
      setTimeout(() => success.remove(), 300);
    }, 1500);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PureOneInteractions();
  });
} else {
  new PureOneInteractions();
}

export default PureOneInteractions;